import { useEffect, useMemo, useState } from "react";
import { Mail, MessageCircle, Phone, Search, X, AlertCircle, Inbox } from "lucide-react";
import AdminHeader from "@/components/admin/AdminHeader";
import { fetchApplications } from "@/lib/authApi";
import { ADMIN } from "@/constants/testIds/admin";
import { buildMailto, buildWhatsApp } from "@/constants/clinic";

const FILTERS = [
    { id: "all", label: "All", testId: ADMIN.listFilterAll },
    { id: "reversal", label: "Reversal", testId: ADMIN.listFilterReversal },
    { id: "longevity", label: "Longevity", testId: ADMIN.listFilterLongevity },
];

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function buildPatientSummary(a) {
    const lines = [
        `Reaching out re: your Discovery Form submission.`,
        ``,
        `Pathway: ${a.objective === "reversal" ? "Disease Reversal" : "Longevity & Optimisation"}`,
    ];
    if (a.objective === "reversal") {
        if (a.reversal_focus?.length) lines.push(`Focus: ${a.reversal_focus.join(", ")}`);
        if (a.reversal_duration) lines.push(`Context: ${a.reversal_duration}`);
    } else {
        if (a.longevity_focus?.length) lines.push(`Focus: ${a.longevity_focus.join(", ")}`);
        if (a.wearable) lines.push(`Wearable: ${a.wearable}`);
    }
    lines.push(`Preferred format: ${a.format}`);
    return lines.join("\n");
}

export default function AdminApplicationsPage({ user }) {
    const [apps, setApps] = useState(null); // null = loading, [] = empty, [...] = loaded
    const [error, setError] = useState(null);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all");
    const [active, setActive] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchApplications();
                if (!cancelled) setApps(data);
            } catch (err) {
                console.error("[admin] fetchApplications failed", err);
                if (!cancelled) setError(err?.response?.status || "network");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const filtered = useMemo(() => {
        if (!apps) return [];
        const q = query.trim().toLowerCase();
        return apps.filter((a) => {
            if (filter !== "all" && a.objective !== filter) return false;
            if (!q) return true;
            return (
                a.name.toLowerCase().includes(q) ||
                a.email.toLowerCase().includes(q) ||
                a.city.toLowerCase().includes(q) ||
                (a.phone || "").toLowerCase().includes(q)
            );
        });
    }, [apps, query, filter]);

    return (
        <div className="min-h-screen bg-[#F8FAFC]">
            <AdminHeader user={user} />

            <main
                data-testid={ADMIN.listPage}
                className="ihx-container py-10 lg:py-14"
            >
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                    <div>
                        <span className="ihx-eyebrow">Discovery Form</span>
                        <h1 className="font-serif text-[30px] md:text-[36px] leading-[1.1] mt-3">
                            Consultation enquiries
                        </h1>
                        <p className="text-[14px] text-slate-500 mt-2">
                            {apps === null
                                ? "Loading clinical baselines…"
                                : `${apps.length} total · ${filtered.length} shown`}
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative">
                            <Search
                                size={14}
                                strokeWidth={1.6}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                            <input
                                data-testid={ADMIN.listSearch}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search name, email, city…"
                                className="ihx-field !pl-9 min-w-[260px]"
                            />
                        </div>
                        <div className="inline-flex border border-slate-200 bg-white">
                            {FILTERS.map((f) => (
                                <button
                                    key={f.id}
                                    type="button"
                                    data-testid={f.testId}
                                    onClick={() => setFilter(f.id)}
                                    className={`px-4 py-2.5 text-[12.5px] tracking-[0.06em] uppercase font-medium transition-colors duration-300 ${
                                        filter === f.id
                                            ? "bg-[#0F172A] text-white"
                                            : "text-slate-600 hover:text-[#0F172A]"
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="mb-8 p-5 border border-[#991B1B] bg-[#FEF2F2] flex items-start gap-3">
                        <AlertCircle
                            size={18}
                            strokeWidth={1.6}
                            className="text-[#991B1B] mt-[2px]"
                        />
                        <div>
                            <div className="text-[14px] text-[#991B1B] font-medium">
                                Unable to load submissions
                            </div>
                            <div className="text-[13px] text-slate-600 mt-1">
                                {error === 401
                                    ? "Your session may have expired. Please sign in again."
                                    : error === 403
                                    ? "Your account isn’t on the admin allow-list."
                                    : "Network or server error. Please refresh the page."}
                            </div>
                        </div>
                    </div>
                )}

                {apps !== null && filtered.length === 0 && !error && (
                    <div
                        data-testid={ADMIN.listEmpty}
                        className="border border-dashed border-slate-300 bg-white p-12 text-center"
                    >
                        <Inbox
                            size={28}
                            strokeWidth={1.4}
                            className="mx-auto text-slate-400"
                        />
                        <div className="font-serif text-[20px] mt-4">
                            No matching enquiries
                        </div>
                        <p className="text-[13.5px] text-slate-500 mt-2 max-w-sm mx-auto">
                            {apps.length === 0
                                ? "When patients complete the Discovery Form, their clinical baselines will appear here."
                                : "Try a different search term or clear the pathway filter."}
                        </p>
                    </div>
                )}

                {filtered.length > 0 && (
                    <div className="bg-white border border-slate-200 overflow-hidden">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left text-[10.5px] tracking-[0.22em] uppercase text-slate-500">
                                    <th className="px-5 py-4 font-medium">Patient</th>
                                    <th className="px-5 py-4 font-medium hidden md:table-cell">
                                        Contact
                                    </th>
                                    <th className="px-5 py-4 font-medium">Pathway</th>
                                    <th className="px-5 py-4 font-medium hidden lg:table-cell">
                                        Format
                                    </th>
                                    <th className="px-5 py-4 font-medium hidden lg:table-cell">
                                        Received
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((a) => (
                                    <tr
                                        key={a.id}
                                        data-testid={ADMIN.listRow}
                                        onClick={() => setActive(a)}
                                        className="border-t border-slate-200 cursor-pointer hover:bg-[#F8FAFC] transition-colors"
                                    >
                                        <td className="px-5 py-4">
                                            <div className="font-serif text-[16px] text-[#0F172A]">
                                                {a.name}
                                            </div>
                                            <div className="text-[12.5px] text-slate-500 mt-0.5">
                                                {a.city}
                                            </div>
                                        </td>
                                        <td className="px-5 py-4 hidden md:table-cell">
                                            <div className="text-[13px] text-slate-700">
                                                {a.email}
                                            </div>
                                            <div className="text-[12.5px] text-slate-500 mt-0.5">
                                                {a.phone}
                                            </div>
                                        </td>
                                        <td className="px-5 py-4">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-1 text-[11px] tracking-[0.12em] uppercase font-medium ${
                                                    a.objective === "reversal"
                                                        ? "bg-[#FEF3C7] text-[#92400E]"
                                                        : "bg-[#0F172A] text-white"
                                                }`}
                                            >
                                                {a.objective}
                                            </span>
                                            <div className="text-[12px] text-slate-500 mt-1.5">
                                                {a.commitment === "yes"
                                                    ? "Ready to begin"
                                                    : "Exploring"}
                                            </div>
                                        </td>
                                        <td className="px-5 py-4 hidden lg:table-cell text-[13px] text-slate-700 capitalize">
                                            {a.format}
                                        </td>
                                        <td className="px-5 py-4 hidden lg:table-cell text-[12.5px] text-slate-500">
                                            {formatDate(a.created_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>

            {active && <DetailDrawer app={active} onClose={() => setActive(null)} />}
        </div>
    );
}

function DetailDrawer({ app, onClose }) {
    const mailtoHref = buildMailto(
        `Re: your enquiry — ${app.name}`,
        buildPatientSummary(app)
    );
    const waHref = buildWhatsApp(buildPatientSummary(app));

    return (
        <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[60] ihx-anim-fade-in"
        >
            <button
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 bg-[#0F172A]/55"
            />
            <aside
                data-testid={ADMIN.detailDrawer}
                className="absolute top-0 right-0 h-full w-full max-w-[480px] bg-white shadow-2xl flex flex-col"
            >
                <header className="flex items-start justify-between p-7 border-b border-slate-200">
                    <div>
                        <span className="ihx-eyebrow">Enquiry detail</span>
                        <h2 className="font-serif text-[24px] mt-2 leading-tight">
                            {app.name}
                        </h2>
                        <div className="text-[12.5px] text-slate-500 mt-1">
                            {app.city} · received {formatDate(app.created_at)}
                        </div>
                    </div>
                    <button
                        type="button"
                        data-testid={ADMIN.detailClose}
                        onClick={onClose}
                        className="text-slate-500 hover:text-[#0F172A] p-2 -mr-2"
                        aria-label="Close drawer"
                    >
                        <X size={20} strokeWidth={1.6} />
                    </button>
                </header>

                <div className="overflow-y-auto flex-1 px-7 py-6 space-y-7">
                    <Field label="Pathway" value={app.objective === "reversal" ? "Disease Reversal" : "Longevity & Optimisation"} />
                    {app.objective === "reversal" ? (
                        <>
                            <Field
                                label="Focus areas"
                                value={(app.reversal_focus || []).join(", ") || "—"}
                            />
                            <Field
                                label="Symptom duration / context"
                                value={app.reversal_duration || "—"}
                                multiline
                            />
                        </>
                    ) : (
                        <>
                            <Field
                                label="Focus areas"
                                value={(app.longevity_focus || []).join(", ") || "—"}
                            />
                            <Field label="Wearable data" value={app.wearable || "—"} />
                        </>
                    )}
                    <Field label="Preferred format" value={app.format} capitalize />
                    <Field
                        label="Commitment"
                        value={app.commitment === "yes" ? "Ready to begin" : "Exploring — wants to learn more"}
                    />
                    <hr className="border-slate-200" />
                    <Field label="Email" value={app.email} />
                    <Field label="Phone" value={app.phone} />
                    <Field label="City" value={app.city} />
                </div>

                <footer className="p-6 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-3 gap-3 bg-[#F8FAFC]">
                    <a
                        data-testid={ADMIN.detailMailto}
                        href={`mailto:${app.email}?${new URLSearchParams({
                            subject: `Re: your enquiry — ${app.name}`,
                            body: buildPatientSummary(app),
                        }).toString()}`}
                        className="ihx-btn-primary justify-center"
                    >
                        <Mail size={15} strokeWidth={1.7} /> Email
                    </a>
                    <a
                        data-testid={ADMIN.detailWhatsapp}
                        href={waHref}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="ihx-btn-secondary justify-center !p-3 border border-slate-300"
                    >
                        <MessageCircle size={15} strokeWidth={1.7} /> WhatsApp
                    </a>
                    <a
                        href={`tel:${app.phone.replace(/\s+/g, "")}`}
                        className="ihx-btn-secondary justify-center !p-3 border border-slate-300"
                    >
                        <Phone size={15} strokeWidth={1.7} /> Call
                    </a>
                    {/* unused refs to satisfy linter */}
                    <span className="hidden">{mailtoHref}</span>
                </footer>
            </aside>
        </div>
    );
}

function Field({ label, value, multiline, capitalize }) {
    return (
        <div>
            <div className="text-[10.5px] tracking-[0.22em] uppercase text-slate-500">
                {label}
            </div>
            <div
                className={`mt-2 text-[14.5px] text-[#0F172A] ${
                    multiline ? "leading-[1.7] whitespace-pre-wrap" : ""
                } ${capitalize ? "capitalize" : ""}`}
            >
                {value}
            </div>
        </div>
    );
}
